import React, { useState, useRef, ChangeEvent, KeyboardEvent } from "react";

interface OTPInputProps {
  length?: number;
  setOTP?: (n: string[]) => void;
  otp?: string[];
  autoFocus?: boolean;
}

const OTPInput: React.FC<OTPInputProps> = ({
  length = 6,
  otp = [],
  setOTP,
  autoFocus = true,
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [internalOtp, setInternalOtp] = useState<string[]>(
    Array.from({ length }, () => "")
  );
  const inputRefs = useRef<HTMLInputElement[]>(new Array(length).fill(null));

  const currentOtp = otp && otp.length ? otp : internalOtp;
  const updateOtp = (next: string[]) => {
    if (setOTP) {
      setOTP(next);
    } else {
      setInternalOtp(next);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 1) {
      const next = [...currentOtp];
      next[index] = value;
      updateOtp(next);
      if (value && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace") {
      if (!currentOtp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
      return;
    }
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
      return;
    }
    if (e.key === "ArrowRight" && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      return;
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData?.getData("text/plain").trim();
    if (!pasteData) return;
    if (/^\d*$/.test(pasteData)) {
      const chars = pasteData.split("").slice(0, length);
      const next = Array.from({ length }, (_, i) => chars[i] || "");
      updateOtp(next);
      const lastFilled = Math.min(chars.length, length) - 1;
      if (lastFilled >= 0) {
        inputRefs.current[lastFilled]?.focus();
      }
    }
  };

  const handleFocus = (index: number) => {
    setActiveIndex(index);
  };

  const handleBlur = () => {
    setActiveIndex(null);
  };

  return (
    <div className="px-10 flex justify-center items-center gap-2 sm:gap-2 2xl:gap-2 py-2">
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(input) => {
            inputRefs.current[index] = input as HTMLInputElement;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          maxLength={1}
          value={currentOtp[index] || ""}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          onBlur={handleBlur}
          className={`w-10 h-10 sm:w-12 sm:h-12 2xl:w-14 2xl:h-14 text-center bg-white border rounded-[10px] text-lg sm:text-xl font-semibold outline-none transition-all
            ${activeIndex === index ? "border-[#DC2626] ring-2 ring-[#DC2626]/20" : "border-[#E5E7EB]"}
          `}
          {...(index === 0 && autoFocus ? { autoFocus: true } : {})}
        />
      ))}
    </div>
  );
};

export default OTPInput;
